import { canCallIn, effectiveLanguage, textCountsByLanguage, reviewedLine } from '../platform-languages';
import type { LanguageCode, TextCount } from '../platform-languages';
import { hasUnknown, resolveTokens, tokenPlain } from '../msg/tokens';
import type { Resolved } from '../msg/tokens';
import { REMINDERS } from './mockData';
import {
  RM_AUDIENCE_LABEL,
  RM_CHANNEL_LABEL,
  RM_FIELDS,
  RM_FIELD_BY_KEY,
} from './types';
import type {
  RmAudience,
  RmChannel,
  RmFieldKey,
  RmPreviewAppointment,
  RmSection,
  RmStep,
  RmToken,
} from './types';

/**
 * Everything the reminders screens say about themselves, **computed**.
 *
 * The old settings page wrote its summaries out by hand — *"call at 12h"* sat in
 * the data beside the offsets it was supposed to describe, so editing the offsets
 * left the sentence behind. Here `checksInWords()` reads the steps.
 */

/* ---- when ---------------------------------------------------------------- */

/** `14 days before` · `2 hours before` · `12 hours after` · `3 days after`. */
export function whenLabel(step: RmStep, kind: RmSection['kind']): string {
  const after = kind === 'post_op' || kind === 'missed' || kind === 'review';
  const unit = step.offset === 1 ? step.unit.replace(/s$/, '') : step.unit;
  return `${String(step.offset)} ${unit} ${after ? 'after' : 'before'}`;
}

/** The sub under the editor header — what *this* offset means in practice. */
export function whenExplanation(step: RmStep, kind: RmSection['kind']): string {
  if (kind === 'post_op') {
    return step.channel === 'ai_call'
      ? 'From the end of a completed visit. Moved to the morning if it falls outside call hours.'
      : 'From the end of a completed visit.';
  }
  if (kind === 'missed') return 'From the appointment’s start time, once it is marked missed.';
  if (kind === 'review') return 'After a completed visit in one of the categories above.';
  if (step.unit === 'hours') return `Sent ${String(step.offset)} hours before the appointment — within sending hours.`;
  return step.offset === 1
    ? 'Sent at the appointment’s time of day, one day earlier — within text sending hours.'
    : `Sent at the appointment’s time of day, ${String(step.offset)} days earlier.`;
}

/* ---- who gets it --------------------------------------------------------- */

/**
 * The words on the step card. Two versions reads as *Everyone · a different
 * message if confirmed*, with the second half in accent — one step, not two.
 */
export function audienceLabel(step: RmStep): string {
  if (step.audience === 'everyone' && step.twoVersions === true) {
    return 'Everyone · a different message if confirmed';
  }
  return RM_AUDIENCE_LABEL[step.audience];
}

/** The short tag on a step card: `2 VERSIONS` · `NOT CONFIRMED` · `CONFIRMED`. */
export function audienceTag(step: RmStep): string | undefined {
  if (step.audience === 'everyone') return step.twoVersions === true ? '2 versions' : undefined;
  return step.audience === 'not_confirmed' ? 'Not confirmed' : 'Confirmed';
}

/**
 * **An AI call can only go to somebody who has not confirmed** (§3) — there is
 * nothing to ring a confirmed patient about, so the other two choices are
 * disabled with the reason rather than hidden.
 */
export function audienceUnavailable(channel: RmChannel, audience: RmAudience): string | undefined {
  if (channel !== 'ai_call') return undefined;
  return audience === 'not_confirmed' ? undefined : 'ELVA only calls patients who haven’t confirmed.';
}

/** The two-version switch exists **only under *Everyone***. */
export function canHaveTwoVersions(step: RmStep): boolean {
  return step.channel !== 'ai_call' && step.audience === 'everyone';
}

/** Which bodies a step actually has — one tab or two. */
export function versionsOf(step: RmStep): ('not_confirmed' | 'confirmed')[] {
  if (step.twoVersions === true && canHaveTwoVersions(step)) return ['not_confirmed', 'confirmed'];
  return ['not_confirmed'];
}

export const VERSION_LABEL: Record<'not_confirmed' | 'confirmed', string> = {
  not_confirmed: 'Not confirmed yet',
  confirmed: 'Already confirmed',
};

/** The body for a version — the single body when there is only one. */
export function bodyFor(step: RmStep, version: 'not_confirmed' | 'confirmed'): RmToken[] {
  if (version === 'confirmed' && step.twoVersions === true) return step.bodyConfirmed ?? [];
  return step.body ?? [];
}

export function subjectFor(step: RmStep, version: 'not_confirmed' | 'confirmed'): RmToken[] {
  if (version === 'confirmed' && step.twoVersions === true) return step.subjectConfirmed ?? step.subject ?? [];
  return step.subject ?? [];
}

/**
 * What ELVA adds to a not-confirmed message, and the practice does not write.
 * A text gets a reply line; an email gets a button and a link.
 */
export function confirmAddition(channel: RmChannel): string | undefined {
  if (channel === 'text') {
    return 'On the Not confirmed yet version, ELVA adds “Reply C to confirm or R to change the time.” to the end.';
  }
  if (channel === 'email') {
    return 'On the Not confirmed yet version, ELVA adds a Confirm my appointment button and a Change the time link.';
  }
  return undefined;
}

/** Whether this version of this step carries the confirm affordances. */
export function showsConfirmAffordance(step: RmStep, version: 'not_confirmed' | 'confirmed'): boolean {
  if (step.audience === 'confirmed') return false;
  return version === 'not_confirmed';
}

/* ---- the card summaries -------------------------------------------------- */

/** `5 of 6 steps · 4 on`. Both halves counted. */
export function beforeFooter(section: RmSection): string {
  const on = section.steps.filter((s) => s.on).length;
  return `${String(section.steps.length)} of ${String(section.maxSteps)} steps · ${String(on)} on · Changes apply to messages not sent yet.`;
}

/**
 * `Call at 12 hours · text at 3 and 10 days` — **read off the checks**, so it
 * cannot describe a set of offsets the section no longer has.
 */
export function checksInWords(section: RmSection): string {
  if (section.steps.length === 0) return 'No checks';
  const calls = section.steps.filter((s) => s.channel === 'ai_call');
  const texts = section.steps.filter((s) => s.channel !== 'ai_call');
  const parts: string[] = [];
  const list = (steps: RmStep[]) => {
    const byUnit = new Map<string, number[]>();
    for (const s of steps) {
      const arr = byUnit.get(s.unit) ?? [];
      arr.push(s.offset);
      byUnit.set(s.unit, arr);
    }
    return [...byUnit.entries()]
      .map(([unit, offsets]) => `${joinNumbers(offsets)} ${offsets.length === 1 && offsets[0] === 1 ? unit.replace(/s$/, '') : unit}`)
      .join(' and ');
  };
  if (calls.length > 0) parts.push(`Call at ${list(calls)}`);
  if (texts.length > 0) parts.push(`${parts.length === 0 ? 'Text' : 'text'} at ${list(texts)}`);
  return parts.join(' · ');
}

/** `3 and 10` · `3, 10 and 14`. */
function joinNumbers(ns: number[]): string {
  const s = ns.map(String);
  if (s.length <= 1) return s[0] ?? '';
  return `${s.slice(0, -1).join(', ')} and ${s[s.length - 1] ?? ''}`;
}

/** The one-line summary on a step card. */
export function stepSummary(step: RmStep): string {
  if (step.mode === 'smart') {
    const brief = (step.brief ?? '').trim();
    return brief === '' ? 'No brief yet' : clip(brief);
  }
  const words = tokenPlain(step.body ?? [], (k) => RM_FIELD_BY_KEY[k].label);
  return words.trim() === '' ? 'No message yet' : clip(words);
}

function clip(s: string): string {
  const one = s.replace(/\s+/g, ' ').trim();
  return one.length > 58 ? `${one.slice(0, 58)}…` : one;
}

/* ---- fields -------------------------------------------------------------- */

/**
 * The `{` menu for a step. **On a call, visit instructions and links are greyed
 * with why** — they cannot be read out, and hiding them makes somebody hunt for
 * a field that was quietly removed.
 */
export function rmFieldMenu(channel: RmChannel) {
  return RM_FIELDS.map((field) => {
    const unreadable =
      channel === 'ai_call' && (field.key === 'visit_instructions' || field.key === 'change_link');
    return { field, unavailable: unreadable ? 'Can’t be read out on a call' : undefined };
  });
}

/** Fill a body in for one appointment. */
export function resolveForAppointment(
  tokens: RmToken[],
  appt: RmPreviewAppointment,
  fallbacks: Partial<Record<RmFieldKey, string>> | undefined,
): Resolved<RmFieldKey> {
  return resolveTokens(tokens, appt.values, fallbacks, RM_FIELDS);
}

/* ---- the counter, per language ------------------------------------------- */

/**
 * `148 characters · English 1 text · Farsi 3 texts` (§3). One number would be
 * wrong for one of the languages: Farsi fits 70 characters a part, not 160.
 */
export function stepTextCounts(step: RmStep, version: 'not_confirmed' | 'confirmed'): TextCount[] {
  if (step.channel !== 'text') return [];
  const words = tokenPlain(bodyFor(step, version), (k) => RM_FIELD_BY_KEY[k].label);
  return textCountsByLanguage(words, reviewedLine('opt_out', 'en'));
}

/* ---- what a patient actually gets --------------------------------------- */

/** Which version this appointment will be sent. */
export function versionForAppointment(
  step: RmStep,
  appt: RmPreviewAppointment,
): 'not_confirmed' | 'confirmed' {
  if (step.twoVersions === true && canHaveTwoVersions(step)) {
    return appt.confirmed ? 'confirmed' : 'not_confirmed';
  }
  return step.audience === 'confirmed' ? 'confirmed' : 'not_confirmed';
}

/** Does this step reach this appointment at all? */
export function stepReaches(step: RmStep, appt: RmPreviewAppointment): boolean {
  if (!step.on) return false;
  if (step.audience === 'not_confirmed') return !appt.confirmed;
  if (step.audience === 'confirmed') return appt.confirmed;
  return true;
}

/**
 * A call in a language ELVA cannot speak yet is **skipped, and the text version
 * sent** — never a silent nothing (§5).
 */
export function callFallsBackToText(step: RmStep, language: LanguageCode): boolean {
  return step.channel === 'ai_call' && !canCallIn(language);
}

/* ---- readiness ----------------------------------------------------------- */

export interface RmIssue {
  stepId: string;
  short: string;
  long: string;
}

/** What stops a section being saved. The footer lists these, or says it is ready. */
export function sectionIssues(section: RmSection): RmIssue[] {
  const out: RmIssue[] = [];
  for (const step of section.steps) {
    if (!step.on) continue;
    const label = `${RM_CHANNEL_LABEL[step.channel]} ${whenLabel(step, section.kind)}`;
    if (step.mode === 'smart') {
      if ((step.brief ?? '').trim() === '') {
        out.push({ stepId: step.id, short: 'Needs a brief', long: `${label} needs a brief` });
      }
      // A post-op call's text version is required — ELVA never calls twice, so
      // without it a patient who does not answer gets nothing at all.
      if (step.channel === 'ai_call' && section.kind === 'post_op' && (step.textVersion ?? []).length === 0) {
        out.push({
          stepId: step.id,
          short: 'Needs a text version',
          long: `${label} needs a text version for when nobody answers`,
        });
      }
    } else {
      for (const version of versionsOf(step)) {
        if (bodyFor(step, version).length === 0) {
          out.push({
            stepId: step.id,
            short: `Needs the ${VERSION_LABEL[version].toLowerCase()} message`,
            long: `${label} has no ${VERSION_LABEL[version].toLowerCase()} message`,
          });
        }
      }
      if (step.channel === 'email' && (step.subject ?? []).length === 0) {
        out.push({ stepId: step.id, short: 'Needs a subject', long: `${label} needs a subject line` });
      }
    }
    if (hasUnknown(step.body, step.bodyConfirmed, step.subject, step.textVersion)) {
      out.push({ stepId: step.id, short: 'Unknown field', long: `${label} has braces that match no field` });
    }
  }
  return out;
}

export function sectionById(key: string): RmSection | undefined {
  return REMINDERS.sections.find((s) => s.key === key);
}

/** Procedures, for card 02 — every post-op section, checks or not. */
export function procedureSections(): RmSection[] {
  return REMINDERS.sections.filter((s) => s.kind === 'post_op');
}

export function beforeSection(): RmSection {
  const found = REMINDERS.sections.find((s) => s.kind === 'before');
  if (found === undefined) throw new Error('Appointment Reminders has no before-the-visit section');
  return found;
}

/** The appointments a preview should use, in the language asked for. */
export function previewLanguage(appt: RmPreviewAppointment, showEnglish: boolean): LanguageCode {
  return showEnglish ? 'en' : effectiveLanguage(appt.language);
}
